// src/pages/tareas/renderers/JuegoRenderer.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { RotateCcw, Check, X, SkipForward } from "lucide-react";

/* =========================
   Config base (CRA / Vite)
   ========================= */
const API_BASE =
  (typeof process !== "undefined" &&
    (process.env.REACT_APP_API_URL || process.env.VITE_API_URL)) || "";

/* =========================
   Estilos unificados y layout fijo (light)
   ========================= */
const GAMI_STYLES = (
  <style>{`
  :root{
    --gami-bg:#ffffff;
    --gami-bg-card:#ffffff;
    --gami-border:#e5e7eb;
    --gami-shadow:0 6px 24px rgba(0,0,0,.06);
    --gami-shadow-hover:0 10px 30px rgba(0,0,0,.08);
    --gami-text:#0f172a;
    --gami-dim:#64748b;
    --gami-primary:#4f46e5;
    --gami-primary-2:#7c3aed;
    --gami-warn:#0ea5e9;
    --gami-danger:#dc2626;
    --gami-radius:16px;
    --gami-measure:70ch;
    --gami-maxw:900px;

    /* alturas y tamaños consistentes */
    --cta-minh:44px;
    --chip-minh:32px;
    --gap:10px;
  }

  .gami-enter{ animation:gami-enter .25s ease both; }
  @keyframes gami-enter{ from{opacity:0; transform: translateY(6px)} to{opacity:1; transform:none} }

  /* Card centrado y responsivo */
  .gami-shell{ margin:0 auto; max-width:min(var(--gami-maxw), 96vw); }

  /* Panel con layout fijo: header (auto) / body scroll (1fr) */
  .gami-panel{
    position:relative;
    display:grid;
    grid-template-rows:auto 1fr;
    border:1px solid var(--gami-border);
    border-radius: var(--gami-radius);
    box-shadow: var(--gami-shadow);
    background:
      radial-gradient(500px 200px at -10% -20%, rgba(15,95,184,0.07), transparent 70%),
      var(--gami-bg-card);
    color:var(--gami-text);
    min-height: clamp(520px, 72vh, 920px); /* tamaño fijo cómodo y grande */
    overflow:hidden;
  }

  /* Head */
  .game-head{
    padding:14px 14px 10px;
    border-bottom:1px solid var(--gami-border);
    background:
      radial-gradient(520px 220px at -20% -30%, rgba(79,70,229,.06), transparent 70%),
      var(--gami-bg);
  }
  .game-headRow{ display:flex; flex-wrap:wrap; justify-content:space-between; align-items:flex-start; gap:8px; }
  .game-headLeft{ flex:1; min-width:0; }
  .gami-qTitle{ margin:0; font-weight:800; letter-spacing:.2px; font-size: clamp(1.02rem, 0.96rem + 0.6vw, 1.22rem); line-height:1.32; word-break:break-word; }
  .game-sub{ font-size:.8rem; line-height:1.3; font-weight:700; color:var(--gami-dim); margin-top:6px; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
  .game-headRight{ display:flex; flex-direction:column; align-items:flex-end; gap:6px; font-size:.75rem; line-height:1.2; text-align:right; }

  .gami-noCoinsBadge{ display:inline-flex; align-items:center; gap:6px; border-radius:9999px; background:#fff1f2; border:1px solid #fecaca; color:#dc2626; font-size:.72rem; font-weight:800; padding:6px 10px; line-height:1.2; min-height: var(--chip-minh); }

  /* Body scroll, mantiene el footer visible con sticky */
  .gami-body{ min-height:0; overflow:auto; padding:12px 14px 0; }
  .gami-inner{
    margin:0 auto; max-width:var(--gami-measure);
    display:flex; flex-direction:column; gap:12px;
  }
  /* área de juego con altura mínima amplia */
  .gami-contentMin{ min-height: clamp(360px, 48vh, 640px); display:flex; flex-direction:column; gap:12px; }

  /* Acciones superiores (comodines / reintentar) */
  .gami-actions{ display:flex; flex-wrap:wrap; gap:var(--gap); align-items:center; }
  .gami-chip{
    display:inline-flex; align-items:center; gap:8px;
    padding:8px 12px; border-radius:12px; font-size:.86rem; font-weight:800;
    border:1px solid var(--gami-border); background:#fff; color:var(--gami-text);
    min-height:var(--chip-minh);
  }
  .gami-chip.warn{ background:#fef3c7; color:#92400e; border-color:#fde68a; }
  .gami-chip.danger{ background:#fee2e2; color:#dc2626; border-color:#fecaca; }

  .gami-btn{
    display:inline-flex; align-items:center; justify-content:center; gap:8px;
    padding:10px 16px; font-size:.9rem; font-weight:900;
    border-radius:12px; border:1px solid var(--gami-border);
    background:#fff; color:#0f172a; cursor:pointer; line-height:1.2;
    transition:transform .12s, box-shadow .15s, background .15s;
    min-height: var(--cta-minh); min-width: 120px;
  }
  .gami-btn:hover{ transform: translateY(-1px); box-shadow: var(--gami-shadow-hover); background:#f8fafc; }
  .gami-btn[disabled]{ opacity:.5; cursor:not-allowed; transform:none; }
  .gami-btn.solid-warn{ background:var(--gami-warn); color:#fff; border-color:var(--gami-warn); box-shadow:0 10px 24px rgba(14,165,233,.18); }
  .gami-btn.solid-danger{ background:var(--gami-danger); color:#fff; border-color:var(--gami-danger); box-shadow:0 10px 24px rgba(220,38,38,.18); }

  /* Alertas */
  .gami-alert{ display:flex; flex-wrap:wrap; align-items:flex-start; gap:8px; padding:12px 14px; border-radius:12px; border:1px solid var(--gami-border); background:#fff; box-shadow:0 1px 2px rgba(0,0,0,.04) inset; font-size:.9rem; line-height:1.45; }
  .gami-alert.success{ background:#ecfdf5; border-color:#bbf7d0; color:#065f46; }
  .gami-alert.error{ background:#fff1f2; border-color:#fecaca; color:#991b1b; }
  .gami-locked-block{ border:1px solid #fecaca; background:#fff1f2; border-radius:12px; padding:12px; color:#991b1b; font-size:.9rem; font-weight:600; line-height:1.45; display:flex; gap:10px; }
  .gami-locked-global{ border:1px solid #fecaca; background:#fff1f2; border-radius:12px; padding:12px; color:#991b1b; font-size:.9rem; font-weight:600; line-height:1.45; }
  .gami-locked-global-title{ font-weight:800; margin-bottom:4px; display:flex; align-items:center; gap:6px; color:#991b1b; }
  .gami-locked-global-sub{ font-size:.85rem; color:#991b1b; }

  /* Footer sticky con acciones a la DERECHA */
  .game-footer{
    position: sticky;
    bottom: -1px;
    z-index: 3;
    margin-top: 14px;
    background: linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,255,255,.98) 40%, rgba(255,255,255,1) 100%);
    backdrop-filter: blur(8px);
    border-top: 1px solid var(--gami-border);
    padding: 10px 8px calc(10px + env(safe-area-inset-bottom));
  }
  .game-actions{ display:flex; flex-wrap:wrap; gap:var(--gap); justify-content:flex-end; }
  .game-pad-bottom{ padding-bottom: 8px; }

  /* Layout Sopa */
  .sopa-layout{ display:flex; flex-direction:column; gap:16px; }
  .sopa-left, .sopa-right{ width:100%; }
  @media(min-width:768px){ .sopa-layout{ flex-direction:row; align-items:flex-start; } .sopa-left{ flex:0 0 60%; } .sopa-right{ flex:1; } }
  .sopa-pregItem{ border:1px solid #e5e7eb; border-radius:12px; background:#fff; padding:12px; font-size:.9rem; line-height:1.45; }
  .sopa-pregItem.ok{ background:#ecfdf5; border-color:#bbf7d0; }
  .sopa-pregItem-title{ font-weight:800; color:#111827; margin-bottom:4px; }
  .sopa-pregItem-ans{ font-size:.78rem; line-height:1.3; letter-spacing:1px; text-transform:uppercase; color:#9ca3af; font-weight:800; }
  .sopa-pregItem-ans.ok{ color:#166534; }

  /* Modal base */
  .gami-modalWrap{ position:fixed; inset:0; background:rgba(0,0,0,.42); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
  .gami-modal{ background:#fff; border-radius:16px; padding:22px; width:min(520px,100%); box-shadow:var(--gami-shadow); text-align:center; }
`}</style>
);

/* Flip para memorama */
const FLIP_STYLES = (
  <style>{`
    .memo-card { perspective: 900px; }
    .memo-inner { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform .35s ease; border-radius: 10px; }
    .memo-card.flipped .memo-inner { transform: rotateY(180deg); }
    .memo-face { position: absolute; inset: 0; display:flex; align-items:center; justify-content:center; backface-visibility:hidden; border-radius:10px; padding:8px; text-align:center; font-size:.85rem; line-height:1.35; font-weight:700; color:#1f2937; }
    .memo-front { background:#f8fafc; border:1px solid #e5e7eb; color:#6b7280; font-size:1.2rem; }
    .memo-back { background:#fff; border:1px solid #c7d2fe; box-shadow:0 4px 16px rgba(0,0,0,.06); transform: rotateY(180deg); word-break:break-word; }
`}</style>
);

/* =========================
   Helpers lógicos / visuales
   ========================= */
function normalize(s = "") {
  return String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

const GAME_TYPE_LABELS = {
  memorama: "Memorama",
  crucigrama: "Crucigrama",
  sopa_letras: "Sopa de letras",
  ahorcado: "Ahorcado",
  rompecabezas: "Rompecabezas",
};

const formatGameType = (tipo = "memorama") => {
  const key = String(tipo || "memorama").toLowerCase().trim();
  return GAME_TYPE_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const isUntitledGameName = (name = "") => {
  const clean = normalize(name);
  return !clean || clean === "juego sin titulo";
};

const BTN_GHOST = {
  padding: "10px 16px",
  borderRadius: 12,
  border: "1px solid var(--gami-border)",
  background: "#fff",
  fontSize: ".9rem",
  fontWeight: 900,
  cursor: "pointer",
  color: "var(--gami-text)",
  minHeight: "var(--cta-minh)"
};

/* Modal base */
const BaseModal = ({ open, children }) => !open ? null : (
  <div className="gami-modalWrap"><div className="gami-modal">{children}</div></div>
);

/* =========================
   Subcomponentes auxiliares
   ========================= */
function Info({ ok }) {
  if (ok == null) return null;
  const good = ok === true;
  return (
    <div className={`gami-alert ${good ? "success" : "error"}`} style={{ marginTop: 12 }}>
      {good ? <Check size={18} /> : <X size={18} />}
      <div style={{ fontWeight: 800 }}>{good ? "¡Completado!" : "No superado"}</div>
    </div>
  );
}
const resolveUploadUrl = (raw = "") => {
  let url = String(raw || "").trim();
  if (!url) return "";
  if (/^uploads\//i.test(url)) url = `/${url}`;
  if (url.startsWith("/uploads")) {
    return API_BASE ? `${API_BASE}${url}` : `${window.location.origin}${url}`;
  }
  return url;
};

function GameFooter({ onSkip, tip, canSkip = true }) {
  return (
    <div className="game-footer">
      <div className="game-actions">
        {canSkip && (
          <button
            type="button"
            className="gami-btn"
            onClick={onSkip}
            title="Saltar este juego"
          >
            <SkipForward size={16} /> Saltar
          </button>
        )}
      </div>
      {tip ? (
        <div style={{ marginTop: 6, fontSize: ".82rem", color: "#6b7280" }}>{tip}</div>
      ) : null}
    </div>
  );
}


/* =========================
   Panel contenedor fijo
   ========================= */
function Panel({ title, subtitle, right, sinGanancias, children }) {
  return (
    <>
      {GAMI_STYLES}
      <div className="gami-shell">
        <div className="gami-panel gami-enter">
          {/* header */}
          <div className="game-head">
            <div className="game-headRow">
              <div className="game-headLeft">
                <h3 className="gami-qTitle">{title}</h3>
              </div>
              {right ? <div className="game-headRight">{right}</div> : null}
            </div>
          </div>
          {/* body scroll + sticky footer adentro */}
          <div className="gami-body">
            <div className="gami-inner">
              <div className="gami-contentMin">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================
   Renderer principal (refactor)
   ========================= */
export default function JuegoRenderer({ game, onResult, controls }) {
  const tipo = String(game?.tipo_juego || "memorama").toLowerCase().trim();
  const instrucciones = game?.instrucciones || game?.descripcion;
  const gameTypeLabel = formatGameType(tipo);
  const title = isUntitledGameName(game?.nombre) ? gameTypeLabel : game.nombre;

  // Estado local propio del juego (bloqueo por fallo del propio juego)
  const [locked, setLocked] = useState(false);
  const [resetKey, setResetKey] = useState("init");
  const [lastOk, setLastOk] = useState(null); // null | true | false
  const [showFailModal, setShowFailModal] = useState(false);

  // Estado/acciones unificadas desde el padre
  const { estado, retry, skip } = controls;
  const { comodines, sinGanancias, bloqueadoGlobal } = estado;
  const sinComodines = comodines <= 0;
  // 👇 NUEVO: vidas y permiso para saltar
  const vidasRestantes = estado?.vidas ?? 0;
  const canSkip = vidasRestantes > 0;


  // Mapeo de renders de juego
  const GameMap = {
    memorama: (p) => <Memorama {...p} />,
    ahorcado: (p) => <Ahorcado {...p} />,
    rompecabezas: (p) => <Rompecabezas {...p} />,
    crucigrama: (p) => <CrucigramaGrid {...p} />,
    sopa_letras: (p) => <SopaLetrasGrid {...p} />,
  };
  const GameComponent = GameMap[tipo];

  // Cuando el juego reporta resultado
  function handleChildResult(ok) {
    setLastOk(ok);
    if (!ok) {
      if (!sinComodines) setLocked(true);
      setShowFailModal(true); // ← abrir modal de fallo
    }
    onResult?.(ok);
  }

  const showReintentar = locked || lastOk === false || bloqueadoGlobal;
  const subtitle = `Juego: ${gameTypeLabel}`;

  return (
    <>
      <Panel title={title} subtitle={subtitle} sinGanancias={sinGanancias}>
        {/* Acciones superiores (comunes) */}
        <div className="gami-actions">
          <span className={"gami-chip " + (sinComodines ? "danger" : comodines === 1 ? "warn" : "")}>
            Comodines: <b style={{ marginLeft: 6 }}>{comodines}</b>
          </span>

          {showReintentar && (
            <button
              className="gami-btn"
              onClick={async () => {
                const r = await retry(); // consume comodín en servidor
                if (r?.ok) {
                  setLocked(false);
                  setLastOk(null);
                  setShowFailModal(false);
                  setResetKey(String(Date.now())); // reset visual del juego
                }
              }}
              disabled={sinComodines}
              title={sinComodines ? "Sin comodines disponibles" : "Reintentar"}
            >
              <RotateCcw size={16} /> Reintentar
            </button>
          )}
        </div>

        {/* Bloqueos / avisos */}
        {locked && !bloqueadoGlobal && !sinComodines && (
          <div className="gami-locked-block">
            <X size={16} />
            <div>
              <b>Intento fallido.</b> Usa <b>Reintentar</b> para continuar.
            </div>
          </div>
        )}
        {bloqueadoGlobal && !sinComodines && (
          <div className="gami-locked-global">
            <div className="gami-locked-global-title">🔒 <span>Todas las preguntas están bloqueadas</span></div>
            <div className="gami-locked-global-sub">Usa <b>Reintentar</b> para desbloquear toda la tarea.</div>
          </div>
        )}

        {/* Contenido del juego */}
        {instrucciones ? <p style={{ color: "#374151", fontSize: ".95rem", lineHeight: 1.5 }}>{instrucciones}</p> : null}

        {GameComponent ? (
          <GameComponent
            key={resetKey}
            configuracion={game?.configuracion}
            instrucciones=""
            onResult={handleChildResult}
            locked={locked}
          />
        ) : (
          <p style={{ color: "#6b7280", fontSize: ".95rem" }}>Tipo de juego no soportado aún.</p>
        )}

        {/* Footer sticky con Saltar (a la derecha) */}
        <GameFooter
          onSkip={() => skip({ locked: locked || bloqueadoGlobal })}
          canSkip={canSkip}
        />
        <div className="game-pad-bottom" />

      </Panel>

      {/* ===================== MODAL DE FALLO ===================== */}
      <BaseModal open={showFailModal}>
        <div style={{ fontSize: 44, marginBottom: 8, lineHeight: 1 }}>❌</div>
        <div style={{ fontSize: "1.05rem", fontWeight: 900, marginBottom: 6, color: "#b91c1c" }}>
          Intento no superado
        </div>
        <p style={{ marginBottom: 14, color: "#374151" }}>
          {canSkip ? (
            <>
              Puedes <b>Reintentar</b> este juego usando un comodín o <b>Saltar</b> para continuar con la siguiente actividad.
            </>
          ) : (
            <>
              Puedes <b>Reintentar</b> este juego usando un comodín para continuar con la siguiente actividad.
            </>
          )}
        </p>


        {canSkip && (
          <div
            style={{
              background: "#fef3c7",
              padding: 12,
              borderRadius: 10,
              border: "1px solid #f59e0b",
              color: "#92400e",
              fontSize: 14,
              textAlign: "left",
              marginBottom: 16,
            }}
          >
            💡 <b>Tip:</b> Si no tienes comodines, aún puedes <b>Saltar</b>. Recibirás nuevos comodines en la siguiente tarea.
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
          <button
            className="gami-btn"
            onClick={() => setShowFailModal(false)}
          >
            Cerrar
          </button>
          {canSkip && (
            <button
              className="gami-btn"
              onClick={() => {
                setShowFailModal(false);
                skip({ locked: locked || bloqueadoGlobal });
              }}
              title="Saltar este juego"
            >
              <SkipForward size={16} /> Saltar
            </button>
          )}

          <button
            className="gami-btn solid-warn"
            disabled={sinComodines}
            title={sinComodines ? "Sin comodines disponibles" : "Reintentar ahora"}
            onClick={async () => {
              const r = await retry();
              if (r?.ok) {
                setLocked(false);
                setLastOk(null);
                setShowFailModal(false);
                setResetKey(String(Date.now()));
              }
            }}
          >
            <RotateCcw size={16} /> Reintentar
          </button>
        </div>
      </BaseModal>
    </>
  );
}

/* ============================================================
   MEMORAMA
   ============================================================ */
function Memorama({ configuracion, instrucciones, onResult, locked }) {
  const pares = configuracion?.pares || [];
  const intentosMax = configuracion?.intentos_maximos ?? 20;

  const makeDeck = useCallback((pairs) => {
    const cards = [];
    pairs.forEach((p) => {
      const pid = p.id ?? Math.random();
      cards.push({ id: `${pid}-q`, pairId: pid, text: p.pregunta, kind: "Q" });
      cards.push({ id: `${pid}-r`, pairId: pid, text: p.respuesta, kind: "R" });
    });
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return cards;
  }, []);

  const [deck] = useState(() => makeDeck(pares));
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [tries, setTries] = useState(0);
  const [finished, setFinished] = useState(null);

  const allSolved = matched.size === (pares?.length || 0);
  useEffect(() => { if (allSolved && finished == null) { setFinished(true); onResult(true); } }, [allSolved, finished, onResult]);

  const clickCard = (idx) => {
    if (locked || finished != null) return;
    if (flipped.includes(idx)) return;
    if (matched.has(deck[idx].pairId)) return;

    const newFlipped = [...flipped, idx];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      const [a, b] = newFlipped.map((i) => deck[i]);
      if (a.pairId === b.pairId && a.kind !== b.kind) {
        setMatched((prev) => new Set(prev).add(a.pairId));
        setFlipped([]);
      } else {
        setTries((t) => t + 1);
        setTimeout(() => setFlipped([]), 650);
      }
    }
  };

  useEffect(() => { if (tries > intentosMax && finished == null) { setFinished(false); onResult(false); } }, [tries, intentosMax, finished, onResult]);

  return (
    <>
      {FLIP_STYLES}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12, fontSize: ".9rem", color: "#374151", fontWeight: 700 }}>
        <span>Pares: {matched.size}/{pares.length}</span>
        <span>Intentos: {tries}/{intentosMax}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(120px,45%), 1fr))", gap: 10 }}>
        {deck.map((c, idx) => {
          const isFlipped = flipped.includes(idx) || matched.has(c.pairId);
          return (
            <button
              key={c.id}
              onClick={() => clickCard(idx)}
              disabled={finished != null}
              className={`memo-card ${isFlipped ? "flipped" : ""}`}
              style={{
                height: 100, borderRadius: 12, border: "1px solid #e5e7eb",
                cursor: finished != null || locked ? "not-allowed" : "pointer",
                boxShadow: isFlipped ? "0 0 0 2px rgba(79,70,229,.25) inset" : "0 4px 16px rgba(0,0,0,.06)",
                padding: 0, background: "transparent", opacity: locked ? .55 : 1
              }}
              title={isFlipped ? c.text : "Carta"}
            >
              <div className="memo-inner">
                <div className="memo-face memo-front">🃏</div>
                <div className="memo-face memo-back">{c.text}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        <button type="button" onClick={() => { setFinished(false); onResult(false); }} style={BTN_GHOST}>Rendirse</button>
      </div>

      <Info ok={finished} />
    </>
  );
}

/* ============================================================
   AHORCADO
   ============================================================ */
function Ahorcado({ configuracion, instrucciones, onResult, locked }) {
  const items = configuracion?.palabras_con_pistas || [];
  const intentosMax = configuracion?.intentos_maximos ?? 6;

  const [index] = useState(() => (items.length ? Math.floor(Math.random() * items.length) : 0));
  const current = items[index] || { palabra: "", pista: "" };

  const [guessed, setGuessed] = useState(new Set());
  const [fails, setFails] = useState(0);
  const [finished, setFinished] = useState(null);

  const letters = "ABCDEFGHIJKLMNÑOPQRSTUVWXYZ".split("");
  const secret = normalize(current.palabra || "").replace(/[^a-zñ]/g, "");

  const display = (current.palabra || "").split("").map((ch) => {
    const nc = normalize(ch);
    return /[a-zñ]/.test(nc) ? (guessed.has(nc) ? ch : "_") : ch;
  });

  const won = secret.length > 0 && secret.split("").every((c) => guessed.has(c) || !/[a-zñ]/.test(c));
  const lost = fails >= intentosMax;

  useEffect(() => { if (won && finished == null) { setFinished(true); onResult(true); } else if (lost && finished == null) { setFinished(false); onResult(false); } }, [won, lost, finished, onResult]);

  const pick = (l) => {
    if (locked || finished != null) return;
    const n = normalize(l);
    if (guessed.has(n)) return;
    setGuessed((prev) => new Set(prev).add(n));
    if (!secret.includes(n)) setFails((f) => f + 1);
  };

  return (
    <>
      {instrucciones ? <p style={{ color: "#374151", fontSize: ".95rem", lineHeight: 1.5, marginBottom: 12 }}>{instrucciones}</p> : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12, fontSize: ".9rem", color: "#374151", fontWeight: 700 }}>
        <span>Errores: {fails}/{intentosMax}</span>
      </div>

      <div style={{ letterSpacing: 4, fontSize: 28, fontWeight: 800, marginBottom: 16, wordBreak: "break-word", color: "#0f172a" }}>
        {display.join(" ")}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {letters.map((l) => (
          <button
            key={l}
            onClick={() => pick(l)}
            disabled={finished != null || guessed.has(normalize(l)) || locked}
            style={{
              padding: "9px 12px", borderRadius: 12, border: "1px solid #e5e7eb",
              background: guessed.has(normalize(l)) ? "#f3f4f6" : "#fff",
              cursor: finished != null || locked ? "not-allowed" : "pointer",
              minWidth: 40, color: "#1f2937", fontWeight: 800, fontSize: ".95rem", lineHeight: 1.2,
              boxShadow: "0 1px 2px rgba(0,0,0,.04) inset", opacity: locked ? .6 : 1
            }}
          >
            {l}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
        <button type="button" onClick={() => { setFinished(false); onResult(false); }} style={BTN_GHOST}>Rendirse</button>
      </div>

      <Info ok={finished} />
    </>
  );
}

/* ============================================================
   ROMPECABEZAS (jigsaw)
   ============================================================ */
function Rompecabezas({ configuracion, instrucciones, onResult, locked }) {
  const rawUrl = [
    configuracion?.media?.url,
    configuracion?.imagen_url,
    configuracion?.image_url,
    configuracion?.imageUrl,
    configuracion?.img_url,
    configuracion?.url,
  ].find((u) => typeof u === "string" && u.trim()) || "";
  const imageUrl = resolveUploadUrl(rawUrl);

  const piezas = Number(configuracion?.piezas) > 0 ? Number(configuracion.piezas) : 16;
  const cols = Math.max(2, Math.round(Math.sqrt(piezas)));
  const rows = cols;

  const containerRef = React.useRef(null);
  const [img, setImg] = React.useState(null);
  const [containerSize, setContainerSize] = React.useState({ w: 0, h: 0 });
  const [boardSize, setBoardSize] = React.useState({ w: 0, h: 0 });
  const [pieces, setPieces] = React.useState([]);
  const [started, setStarted] = React.useState(false);
  const [solved, setSolved] = React.useState(false);
  const [drag, setDrag] = React.useState(null);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const dist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

  const generateEdgeMap = React.useCallback(() => {
    const edges = [];
    for (let r = 0; r < rows; r++) {
      edges[r] = [];
      for (let c = 0; c < cols; c++) {
        const cell = { top: 0, right: 0, bottom: 0, left: 0 };
        if (r === 0) cell.top = 0; else cell.top = -edges[r - 1][c].bottom;
        if (c === 0) cell.left = 0; else cell.left = -edges[r][c - 1].right;
        if (c === cols - 1) cell.right = 0; else cell.right = Math.random() > 0.5 ? 1 : -1;
        if (r === rows - 1) cell.bottom = 0; else cell.bottom = Math.random() > 0.5 ? 1 : -1;
        edges[r][c] = cell;
      }
    }
    return edges;
  }, [cols, rows]);

  const buildPiecePath = (pw, ph, tab) => {
    const p = new Path2D();
    const K = 0.3;
    const sidePath = (x0, y0, x1, y1, dir) => {
      if (y0 === y1) {
        const y = y0; const xmid = (x0 + x1) / 2; const dx = x1 - x0; const sign = Math.sign(dx); const ctrl = tab * K;
        if (dir === 0) { p.lineTo(x1, y1); }
        else {
          p.lineTo(xmid - (tab * 0.6) * sign, y);
          p.bezierCurveTo(xmid - ctrl * sign, y - dir * tab, xmid + ctrl * sign, y - dir * tab, xmid + (tab * 0.6) * sign, y);
          p.lineTo(x1, y1);
        }
      } else {
        const x = x0; const ymid = (y0 + y1) / 2; const dy = y1 - y0; const sign = Math.sign(dy); const ctrl = tab * K;
        if (dir === 0) { p.lineTo(x1, y1); }
        else {
          p.lineTo(x, ymid - (tab * 0.6) * sign);
          p.bezierCurveTo(x - dir * tab, ymid - ctrl * sign, x - dir * tab, ymid + ctrl * sign, x, ymid + (tab * 0.6) * sign);
          p.lineTo(x1, y1);
        }
      }
    };
    p.moveTo(0, 0);
    return { p, sidePath };
  };

  const drawPiece = (canvas, img, sx, sy, sw, sh, shape, scale = 1) => {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save(); ctx.scale(scale, scale); ctx.clip(shape);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    ctx.restore();
    ctx.save(); ctx.scale(scale, scale); ctx.lineWidth = 2; ctx.strokeStyle = "rgba(0,0,0,0.25)"; ctx.stroke(shape); ctx.restore();
  };

  React.useEffect(() => {
    if (!imageUrl) return;
    const img1 = new Image();
    img1.crossOrigin = "anonymous";
    img1.onload = () => setImg(img1);
    img1.onerror = () => {
      const img2 = new Image();
      img2.onload = () => setImg(img2);
      img2.onerror = () => console.error("No se pudo cargar la imagen del rompecabezas:", imageUrl);
      img2.src = imageUrl;
    };
    img1.src = imageUrl;
  }, [imageUrl]);

  React.useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    if (!img || !containerSize.w) return;

    const maxW = Math.min(containerSize.w, 900);
    const scale = maxW / img.width;
    const W = Math.round(img.width * scale);
    const H = Math.round(img.height * scale);
    setBoardSize({ w: W, h: H });

    const pw = Math.round(W / cols);
    const ph = Math.round(H / rows);
    const tab = Math.round(Math.min(pw, ph) * 0.25);

    const edges = generateEdgeMap();

    const created = [];
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const { p, sidePath } = buildPiecePath(pw, ph, tab);
      sidePath(0, 0, pw, 0, edges[r][c].top);
      sidePath(pw, 0, pw, ph, edges[r][c].right);
      sidePath(pw, ph, 0, ph, edges[r][c].bottom);
      sidePath(0, ph, 0, 0, edges[r][c].left);
      p.closePath();

      const pad = Math.round(tab * 1.1);
      const cw = pw + pad * 2;
      const ch = ph + pad * 2;

      const canvas = document.createElement("canvas");
      canvas.width = cw; canvas.height = ch;

      const ctx = canvas.getContext("2d");
      ctx.translate(pad, pad);

      const sx = Math.round((c * pw) / scale);
      const sy = Math.round((r * ph) / scale);
      const sw = Math.round(pw / scale);
      const sh = Math.round(ph / scale);

      drawPiece(canvas, img, sx, sy, sw, sh, p, 1);

      const tx = c * pw - pad;
      const ty = r * ph - pad;

      const margin = 24;
      const rx = Math.floor(Math.random() * Math.max(1, containerSize.w - cw - margin));
      const ry = Math.floor(Math.random() * Math.max(1, Math.max(H + margin, containerSize.h - ch - margin)));

      created.push({ id: `${r}-${c}`, r, c, canvas, w: cw, h: ch, tx, ty, x: rx, y: ry, snapped: false, pad });
    }
    setPieces(created);
    setStarted(false);
    setSolved(false);
  }, [img, containerSize.w, containerSize.h, cols, rows, generateEdgeMap]);

  React.useEffect(() => {
    const host = containerRef.current?.querySelector(".rpz-pieces");
    if (!host) return;
    host.innerHTML = "";
    const els = [];
    for (const p of pieces) {
      const el = p.canvas;
      el.style.position = "absolute";
      el.style.left = `${p.x}px`;
      el.style.top = `${p.y}px`;
      el.style.cursor = locked || p.snapped ? "default" : "grab";
      el.style.touchAction = "none";
      el.draggable = false;
      host.appendChild(el);
      els.push(el);
    }
    return () => { for (const el of els) if (el.parentNode === host) host.removeChild(el); };
  }, [pieces, locked]);

  React.useEffect(() => {
    const host = containerRef.current?.querySelector(".rpz-pieces");
    if (!host) return;

    const SNAP = Math.max(10, Math.floor(Math.min(boardSize.w, boardSize.h) * 0.02));
    const findPieceByCanvas = (target) => pieces.find(p => p.canvas === target);

    const onPointerDown = (e) => {
      if (locked || solved) return;
      const target = e.target;
      if (!(target instanceof HTMLCanvasElement)) return;
      e.preventDefault();

      const rect = host.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      const piece = findPieceByCanvas(target);
      if (!piece || piece.snapped) return;

      const offsetX = px - piece.x;
      const offsetY = py - piece.y;

      target.style.zIndex = String(100000 + Date.now() % 10000);
      target.style.cursor = "grabbing";

      setDrag({ id: piece.id, offsetX, offsetY });
      if (!started) setStarted(true);
    };

    const onPointerMove = (e) => {
      if (!drag) return;
      const piece = pieces.find(p => p.id === drag.id);
      if (!piece) return;

      const rect = host.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      let nx = px - drag.offsetX;
      let ny = py - drag.offsetY;

      nx = clamp(nx, -piece.w, Math.max(boardSize.w, containerSize.w) - piece.w + piece.pad);
      ny = clamp(ny, -piece.h, Math.max(boardSize.h, containerSize.h) - piece.h + piece.pad);

      piece.x = nx;
      piece.y = ny;
      piece.canvas.style.left = `${nx}px`;
      piece.canvas.style.top = `${ny}px`;
    };

    const onPointerUp = () => {
      if (!drag) return;
      const piece = pieces.find(p => p.id === drag.id);
      if (!piece) { setDrag(null); return; }

      const centerX = piece.x + piece.w / 2;
      const centerY = piece.y + piece.h / 2;
      const targetX = piece.tx + piece.w / 2;
      const targetY = piece.ty + piece.h / 2;

      if (dist(centerX, centerY, targetX, targetY) <= SNAP) {
        piece.x = piece.tx; piece.y = piece.ty; piece.snapped = true;
        piece.canvas.style.left = `${piece.tx}px`;
        piece.canvas.style.top = `${piece.ty}px`;
        piece.canvas.style.cursor = "default";
        piece.canvas.style.zIndex = "1";
      } else {
        piece.canvas.style.cursor = "grab";
      }

      setDrag(null);

      const allSnapped = pieces.every(p => p.snapped);
      if (allSnapped && !solved) { setSolved(true); onResult?.(true); }
    };

    // mouse
    host.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    // touch
    host.addEventListener("touchstart", (e) => {
      if (locked || solved) return;
      if (e.target instanceof HTMLCanvasElement) {
        const t = e.touches[0]; if (!t) return;
        const fake = { target: e.target, clientX: t.clientX, clientY: t.clientY, preventDefault() { } };
        onPointerDown(fake);
      }
    }, { passive: false });
    window.addEventListener("touchmove", (e) => {
      if (!drag) return;
      const t = e.touches[0]; if (!t) return;
      const fake = { clientX: t.clientX, clientY: t.clientY };
      onPointerMove(fake);
    }, { passive: false });
    window.addEventListener("touchend", onPointerUp, { passive: false });

    return () => {
      host.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
    };
  }, [pieces, drag, boardSize, containerSize, solved, cols, rows, onResult, locked]);

  return (
    <div className="rpz-wrap">
      <div
        ref={containerRef}
        className="rpz-container"
        style={{
          position: "relative", width: "100%", minHeight: Math.max(420, boardSize.h + 120),
          borderRadius: 12, border: "1px solid #e5e7eb",
          background: "linear-gradient(180deg, #f8fafc, #eef2f7)", opacity: locked ? 0.6 : 1
        }}
      >
        <div
          className="rpz-board"
          style={{
            position: "absolute", left: 12, right: 12, top: 12,
            width: boardSize.w ? `${boardSize.w}px` : "auto",
            height: boardSize.h ? `${boardSize.h}px` : "auto",
            margin: "0 auto", borderRadius: 10, border: "2px dashed rgba(0,0,0,0.1)",
            background: img ? `url("${imageUrl}") center/contain no-repeat` : "rgba(0,0,0,0.03)"
          }}
        />
        <div className="rpz-pieces" style={{ position: "absolute", inset: 0 }} />
      </div>
      <style>{`
        .rpz-wrap { display: grid; gap: 10px; }
        .rpz-container canvas { user-select: none; }
        @media (max-width: 768px){
          .rpz-wrap { gap: 8px; }
        }
      `}</style>
    </div>
  );
}

/* ============================================================
   CRUCIGRAMA
   ============================================================ */
function CrucigramaGrid({ configuracion, instrucciones, onResult, locked }) {
  const toLetters = (s = "") =>
    String(s).toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-ZÑ]/g, "");
  const canonical = (s = "") => toLetters(s).replace(/\s+/g, "");

  const words = useMemo(() => {
    if (Array.isArray(configuracion?.entradas) && configuracion.entradas.length) {
      return configuracion.entradas.map((e, i) => ({
        id: e.id ?? i + 1,
        clue: String(e.pista || "").trim(),
        answerRaw: String(e.respuesta || "").trim(),
        answer: canonical(e.respuesta || "")
      })).filter(w => w.answer.length >= 2);
    }
    if (Array.isArray(configuracion?.palabras) && configuracion.palabras.length) {
      return configuracion.palabras.map((p, i) => ({
        id: p.id ?? i + 1,
        clue: String(p.pista || p.concepto || p.clue || "").trim(),
        answerRaw: String(p.respuesta || p.palabra || p.answer || "").trim(),
        answer: canonical(p.respuesta || p.palabra || p.answer || "")
      })).filter(w => w.answer.length >= 2);
    }
    return [];
  }, [configuracion]);

  const buildCrossword = useCallback((inp) => {
    if (!inp.length) return { cells: new Map(), placed: [], minR: 0, maxR: -1, minC: 0, maxC: -1 };
    const pool = [...inp].sort((a, b) => b.answer.length - a.answer.length);
    const cells = new Map(); const placed = [];
    const K = (r, c) => `${r},${c}`;
    const get = (r, c) => cells.get(K(r, c));
    const set = (r, c, ch) => cells.set(K(r, c), ch);
    const has = (r, c) => cells.has(K(r, c));

    const aroundFreeFor = (r, c, dir) => dir === "A" ? get(r - 1, c) == null && get(r + 1, c) == null : get(r, c - 1) == null && get(r, c + 1) == null;
    const endsFree = (r, c, dir, len) => dir === "A" ? get(r, c - 1) == null && get(r, c + len) == null : get(r - 1, c) == null && get(r + len, c) == null;
    const canPlace = (r0, c0, dir, text) => {
      const len = text.length;
      for (let i = 0; i < len; i++) {
        const r = dir === "A" ? r0 : r0 + i;
        const c = dir === "A" ? c0 + i : c0;
        const cell = get(r, c);
        if (cell && cell !== text[i]) return null;
        const isOverlap = cell === text[i];
        if (!isOverlap && !aroundFreeFor(r, c, dir)) return null;
      }
      if (!endsFree(r0, c0, dir, len)) return null;
      const out = [];
      for (let i = 0; i < len; i++) out.push({ r: dir === "A" ? r0 : r0 + i, c: dir === "A" ? c0 + i : c0, ch: text[i] });
      return out;
    };

    const first = pool.shift();
    for (let i = 0; i < first.answer.length; i++) set(0, i, first.answer[i]);
    placed.push({ id: first.id, clue: first.clue, answer: first.answer, answerRaw: first.answerRaw, dir: "A", start: { r: 0, c: 0 }, cells: Array.from({ length: first.answer.length }, (_, i) => ({ r: 0, c: i })) });

    const allKeysByLetter = () => {
      const map = new Map();
      for (const [k, ch] of cells.entries()) {
        if (!map.has(ch)) map.set(ch, []);
        map.get(ch).push(k);
      }
      return map;
    };

    const tryPlaceWord = (w) => {
      const letterMap = allKeysByLetter();
      let best = null;
      for (let i = 0; i < w.answer.length; i++) {
        const ch = w.answer[i];
        const spots = letterMap.get(ch) || [];
        for (const spot of spots) {
          const [sr, sc] = spot.split(",").map(Number);
          for (const dir of ["A", "D"]) {
            const r0 = dir === "A" ? sr : sr - i;
            const c0 = dir === "A" ? sc - i : sc;
            const path = canPlace(r0, c0, dir, w.answer);
            if (!path) continue;
            const score = path.filter((p) => get(p.r, p.c) === p.ch).length;
            if (!best || score > best.score) best = { score, dir, r0, c0, path };
          }
        }
      }
      if (!best) {
        let baseR = 2;
        for (; ; baseR += 2) {
          const path = canPlace(baseR, 0, "A", w.answer);
          if (path) { best = { score: 0, dir: "A", r0: baseR, c0: 0, path }; break; }
          if (baseR > 200) break;
        }
      }
      if (!best) return false;
      for (const p of best.path) set(p.r, p.c, p.ch);
      placed.push({ id: w.id, clue: w.clue, answer: w.answer, answerRaw: w.answerRaw, dir: best.dir, start: { r: best.r0, c: best.c0 }, cells: best.path.map(({ r, c }) => ({ r, c })) });
      return true;
    };

    for (const w of pool) tryPlaceWord(w);

    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    for (const k of cells.keys()) {
      const [r, c] = k.split(",").map(Number);
      minR = Math.min(minR, r); maxR = Math.max(maxR, r);
      minC = Math.min(minC, c); maxC = Math.max(maxC, c);
    }
    if (!isFinite(minR)) { minR = 0; maxR = -1; minC = 0; maxC = -1; }
    return { cells, placed, minR, maxR, minC, maxC };
  }, []);

  const { cells, placed, minR, maxR, minC, maxC } = useMemo(() => buildCrossword(words), [words, buildCrossword]);
  const rows = Math.max(0, maxR - minR + 1);
  const cols = Math.max(0, maxC - minC + 1);

  const K = (r, c) => `${r},${c}`;
  const has = (r, c) => cells.has(K(r, c));

  const numbers = new Map();
  let counter = 1;
  for (let r = minR; r <= maxR; r++) for (let c = minC; c <= maxC; c++) {
    if (!has(r, c)) continue;
    const startAcross = !has(r, c - 1) && has(r, c + 1);
    const startDown = !has(r - 1, c) && has(r + 1, c);
    if (startAcross || startDown) numbers.set(K(r, c), counter++);
  }

  const [inputs, setInputs] = useState(() => {
    const m = new Map();
    for (let r = minR; r <= maxR; r++) for (let c = minC; c <= maxC; c++) if (has(r, c)) m.set(K(r, c), "");
    return m;
  });
  useEffect(() => {
    const m = new Map();
    for (let r = minR; r <= maxR; r++) for (let c = minC; c <= maxC; c++) if (has(r, c)) m.set(K(r, c), "");
    setInputs(m);
  }, [minR, maxR, minC, maxC]); // reset

  const inputRefs = useRef(new Map());
  const [sel, setSel] = useState(null); // {r,c,dir}
  const focusCell = (r, c) => { const el = inputRefs.current.get(K(r, c)); if (el) { el.focus(); el.select(); } };
  useEffect(() => { if (sel) focusCell(sel.r, sel.c); }, [sel]);

  const indexByCell = useMemo(() => {
    const m = new Map();
    for (const w of placed) for (const cell of w.cells) {
      const key = K(cell.r, cell.c);
      const slot = m.get(key) || {};
      if (w.dir === "A") slot.across = w; else slot.down = w;
      m.set(key, slot);
    }
    return m;
  }, [placed]);

  const selectDefault = (r, c) => {
    const slot = indexByCell.get(K(r, c)) || {};
    if (slot.across) setSel({ r, c, dir: "A" });
    else if (slot.down) setSel({ r, c, dir: "D" });
    else setSel(null);
  };
  const toggleDirAt = (r, c) => {
    const slot = indexByCell.get(K(r, c)) || {};
    if (slot.across && slot.down) setSel(prev => ({ r, c, dir: prev?.dir === "A" ? "D" : "A" }));
    else if (slot.across) setSel({ r, c, dir: "A" });
    else if (slot.down) setSel({ r, c, dir: "D" });
  };
  const wordPath = (r, c, dir) => {
    let rr = r, cc = c; const stepR = dir === "A" ? 0 : -1; const stepC = dir === "A" ? -1 : 0;
    while (has(rr + stepR, cc + stepC)) { rr += stepR; cc += stepC; }
    const fwR = dir === "A" ? 0 : 1; const fwC = dir === "A" ? 1 : 0;
    const path = []; while (has(rr, cc)) { path.push({ r: rr, c: cc }); rr += fwR; cc += fwC; }
    return path;
  };
  const move = (r, c, dir, delta) => {
    const path = wordPath(r, c, dir);
    const idx = path.findIndex((p) => p.r === r && p.c === c);
    const next = path[idx + delta];
    if (next) setSel({ r: next.r, c: next.c, dir });
  };

  const setChar = (r, c, ch) => {
    if (locked) return;
    const val = toLetters(ch).slice(-1);
    setInputs(prev => { const m = new Map(prev); m.set(K(r, c), val || ""); return m; });
  };

  const allFilledCorrect = useMemo(() => {
    if (rows === 0 || cols === 0) return false;
    for (let r = minR; r <= maxR; r++) for (let c = minC; c <= maxC; c++) {
      if (!has(r, c)) continue;
      const expected = cells.get(K(r, c));
      const typed = (inputs.get(K(r, c)) || "").toUpperCase();
      if (typed !== expected) return false;
    }
    return true;
  }, [inputs, cells, minR, maxR, minC, maxC, rows, cols]);

  const [finished, setFinished] = useState(null);

  useEffect(() => {
    if (allFilledCorrect && finished == null) {
      setFinished(true);
      onResult?.(true);
    }
  }, [allFilledCorrect, finished, onResult]);

  const cellStyle = { width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" };
  const inputStyle = { width: "100%", height: "100%", border: "none", textAlign: "center", fontWeight: 800, fontSize: 16, outline: "none", textTransform: "uppercase", background: "transparent", color: "#111827" };

  const Board = (
    <div style={{ display: "inline-block", padding: 12, borderRadius: 12, border: "3px solid #067e08ff", background: "#cce2d9ff", boxShadow: "0 1px 2px rgba(0,0,0,.04)", opacity: locked ? .6 : 1 }}>
      {rows > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 36px)`, border: "3px solid #067e08ff", borderRadius: 8, overflow: "hidden", userSelect: "none" }}>
          {Array.from({ length: rows * cols }, (_, i) => {
            const r = minR + Math.floor(i / cols);
            const c = minC + (i % cols);
            const key = `${r},${c}`;
            if (!has(r, c)) return <div key={key} style={{ width: 36, height: 36 }} />;
            const num = numbers.get(key);
            const isSel = sel && sel.r === r && sel.c === c;
            const path = sel ? wordPath(sel.r, sel.c, sel.dir) : [];
            const inActiveWord = path.some((p) => p.r === r && p.c === c);
            const expected = cells.get(key);
            const given = (inputs.get(key) || "").toUpperCase();
            const correct = given && given === expected;
            return (
              <div
                key={key}
                style={{
                  ...cellStyle,
                  background: correct ? "#dcfce7" : inActiveWord ? "#e0f2fe" : "white",
                  outline: isSel ? "2px solid #0ea5e9" : "none",
                  outlineOffset: -2,
                  borderRight: "1px solid #e5e7eb",
                  borderBottom: "1px solid #e5e7eb"
                }}
                onClick={() => selectDefault(r, c)}
              >
                {num ? <div style={{ position: "absolute", top: 2, left: 4, fontSize: 10, color: "#6b7280", fontWeight: 700 }}>{num}</div> : null}
                <input
                  ref={(el) => { if (el) inputRefs.current.set(key, el); else inputRefs.current.delete(key); }}
                  value={inputs.get(key) || ""}
                  readOnly={locked}
                  disabled={locked}
                  onChange={(e) => setChar(r, c, e.target.value)}
                  onFocus={() => selectDefault(r, c)}
                  onKeyDown={(e) => {
                    if (locked) return;
                    const here = sel && sel.r === r && sel.c === c ? sel : { r, c, dir: indexByCell.get(key)?.across ? "A" : "D" };
                    const dir = here.dir;
                    if (/^[a-zA-ZñÑ]$/.test(e.key)) { setChar(r, c, e.key); setTimeout(() => move(r, c, dir, 1), 0); }
                    else if (e.key === "Backspace") { if ((inputs.get(key) || "") === "") setTimeout(() => move(r, c, dir, -1), 0); else setChar(r, c, ""); }
                    else if (e.key === " " || e.key === "Tab") { e.preventDefault(); toggleDirAt(r, c); }
                    else if (e.key === "ArrowRight") { e.preventDefault(); move(r, c, "A", 1); }
                    else if (e.key === "ArrowLeft") { e.preventDefault(); move(r, c, "A", -1); }
                    else if (e.key === "ArrowDown") { e.preventDefault(); move(r, c, "D", 1); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); move(r, c, "D", -1); }
                  }}
                  style={inputStyle}
                  maxLength={1}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="gami-alert error">No se pudo generar el crucigrama con las entradas dadas.</div>
      )}
    </div>
  );

  return (
    <>
      {instrucciones ? <p style={{ color: "#374151", fontSize: ".95rem", lineHeight: 1.5, marginBottom: 12 }}>{instrucciones}</p> : null}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap", fontSize: ".9rem", color: "#374151", fontWeight: 700 }}>
        <span>Palabras: {placed.length}</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 16, flexDirection: "column" }}>
          <div style={{ overflowX: "auto" }}>{Board}</div>
          <div>
            <h3 style={{ fontWeight: 800, marginBottom: 6 }}>Pistas</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Horizontales</div>
                <ul style={{ display: "grid", gap: 6 }}>
                  {placed.filter(w => w.dir === "A").map((w) => {
                    const number = numbers.get(K(w.cells[0].r, w.cells[0].c));
                    return <li key={w.id}><b>{number}.</b> {w.clue}</li>;
                  })}
                </ul>
              </div>
              <div>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Verticales</div>
                <ul style={{ display: "grid", gap: 6 }}>
                  {placed.filter(w => w.dir === "D").map((w) => {
                    const number = numbers.get(K(w.cells[0].r, w.cells[0].c));
                    return <li key={w.id}><b>{number}.</b> {w.clue}</li>;
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Info ok={finished} />
    </>
  );
}

/* ============================================================
   SOPA DE LETRAS
   ============================================================ */
function SopaLetrasGrid({ configuracion, instrucciones, onResult, locked }) {
  const ABC = "ABCDEFGHIJKLMNOPQRSTUVWXYZÑ";
  const normClean = (s = "") => String(s).toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-ZÑ]/g, "");

  const rawItems = useMemo(() => {
    const items = [];
    if (Array.isArray(configuracion?.items) && configuracion.items.length) {
      configuracion.items.forEach((it) => items.push({ pregunta: String(it.pregunta || "").trim(), respuesta: normClean(it.respuesta || ""), originalRespuesta: String(it.respuesta || "").trim() }));
    } else if (Array.isArray(configuracion?.preguntas) && Array.isArray(configuracion?.palabras) && configuracion.palabras.length) {
      const n = configuracion.palabras.length;
      for (let i = 0; i < n; i++) {
        const q = String(configuracion.preguntas[i] ?? "").trim();
        const ans = String(configuracion.palabras[i] ?? "").trim();
        items.push({ pregunta: q || `Encuentra: ${ans}`, respuesta: normClean(ans), originalRespuesta: ans });
      }
    } else {
      (configuracion?.palabras || []).map(String).filter(Boolean).forEach((ans) => items.push({ pregunta: `Encuentra: ${ans}`, respuesta: normClean(ans), originalRespuesta: ans }));
    }
    return items.filter((it) => it.respuesta.length > 0);
  }, [configuracion]);

  const rows = Math.max(5, Number(configuracion?.tamaño_tablero?.filas || 15));
  const cols = Math.max(5, Number(configuracion?.tamaño_tablero?.columnas || 15));

  const allowed = useMemo(() => {
    const dirs = configuracion?.direcciones || ["horizontal", "vertical", "diagonal"];
    return { H: dirs.includes("horizontal"), V: dirs.includes("vertical"), D: dirs.includes("diagonal") };
  }, [configuracion?.direcciones]);

  const genBoard = useCallback(() => {
    const grid = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
    const placements = [];
    const dirList = [];
    if (allowed.H) { dirList.push([0, 1]); dirList.push([0, -1]); }
    if (allowed.V) { dirList.push([1, 0]); dirList.push([-1, 0]); }
    if (allowed.D) { dirList.push([1, 1]); dirList.push([-1, -1]); dirList.push([1, -1]); dirList.push([-1, 1]); }

    function canPlace(r, c, dr, dc, text) {
      let rr = r, cc = c;
      for (let i = 0; i < text.length; i++) {
        if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) return false;
        const cell = grid[rr][cc];
        if (cell !== "" && cell !== text[i]) return false;
        rr += dr; cc += dc;
      }
      return true;
    }
    function doPlace(r, c, dr, dc, text, index) {
      const cells = []; let rr = r, cc = c;
      for (let i = 0; i < text.length; i++) { grid[rr][cc] = text[i]; cells.push({ r: rr, c: cc }); rr += dr; cc += dc; }
      placements.push({ index, cells, text });
    }

    rawItems.forEach((w, idx) => {
      const variants = [w.respuesta, w.respuesta.split("").reverse().join("")];
      let placed = false;
      for (let attempt = 0; attempt < 500 && !placed; attempt++) {
        const text = variants[Math.floor(Math.random() * variants.length)];
        const pick = dirList[Math.floor(Math.random() * dirList.length)] || [0, 1];
        const [dr, dc] = pick;

        const maxR = dr === 1 ? rows - text.length : dr === -1 ? rows - 1 : rows - 1;
        const minR = dr === -1 ? text.length - 1 : 0;
        const maxC = dc === 1 ? cols - text.length : dc === -1 ? cols - 1 : cols - 1;
        const minC = dc === -1 ? text.length - 1 : 0;

        const r0 = Math.floor(Math.random() * (maxR - minR + 1)) + minR;
        const c0 = Math.floor(Math.random() * (maxC - minC + 1)) + minC;

        if (canPlace(r0, c0, dr, dc, text)) { doPlace(r0, c0, dr, dc, text, idx); placed = true; }
      }
    });

    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) if (!grid[r][c]) grid[r][c] = ABC[Math.floor(Math.random() * ABC.length)];
    return { grid, placements };
  }, [rows, cols, allowed, rawItems, ABC]);

  const [{ grid, placements }, setBoard] = useState(() => genBoard());
  const [foundFlags, setFoundFlags] = useState(() => rawItems.map(() => false));
  const [finished, setFinished] = useState(null);

  const [dragStart, setDragStart] = useState(null);
  const [dragPath, setDragPath] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const allFound = foundFlags.every(Boolean);
  useEffect(() => { if (allFound && finished == null) { setFinished(true); onResult(true); } }, [allFound, finished, onResult]);

  const permanent = useMemo(() => {
    const s = new Set();
    placements.forEach((pl) => { if (foundFlags[pl.index]) { pl.cells.forEach(({ r, c }) => s.add(`${r},${c}`)); } });
    return s;
  }, [placements, foundFlags]);

  const allowedDirection = (dr, dc) => {
    const sdr = Math.sign(dr), sdc = Math.sign(dc);
    if (sdr === 0 && sdc !== 0) return allowed.H;
    if (sdr !== 0 && sdc === 0) return allowed.V;
    if (Math.abs(dr) === Math.abs(dc)) return allowed.D;
    return false;
  };
  const linePath = (r0, c0, r1, c1) => {
    const dr = r1 - r0, dc = c1 - c0;
    if (!allowedDirection(dr, dc)) return [];
    const sr = Math.sign(dr), sc = Math.sign(dc);
    if (sr === 0 && sc !== 0) { const inc = sc; const path = []; for (let c = c0; inc > 0 ? c <= c1 : c >= c1; c += inc) path.push({ r: r0, c }); return path; }
    if (sc === 0 && sr !== 0) { const inc = sr; const path = []; for (let r = r0; inc > 0 ? r <= r1 : r >= r1; r += inc) path.push({ r, c: c0 }); return path; }
    if (Math.abs(dr) === Math.abs(dc)) { const len = Math.abs(dr); const path = []; for (let i = 0; i <= len; i++) path.push({ r: r0 + i * sr, c: c0 + i * sc }); return path; }
    return [];
  };

  const tryMatch = (path) => {
    if (!path.length) return false;
    const clean = path.map(({ r, c }) => grid[r][c]).join("");
    const reversed = clean.split("").reverse().join("");
    for (const pl of placements) {
      if (foundFlags[pl.index]) continue;
      const forwardEqual = pl.cells.length === path.length && pl.cells.every((cell, i) => cell.r === path[i].r && cell.c === path[i].c);
      const backwardEqual = pl.cells.length === path.length && pl.cells.every((cell, i) => { const j = path.length - 1 - i; return cell.r === path[j].r && cell.c === path[j].c; });
      if (forwardEqual || backwardEqual) {
        if (clean === pl.text || reversed === pl.text) {
          setFoundFlags((prev) => prev.map((v, idx) => (idx === pl.index ? true : v)));
          return true;
        }
      }
    }
    return false;
  };

  const onCellDown = (r, c) => { if (locked) return; setIsDragging(true); setDragStart({ r, c }); setDragPath([{ r, c }]); };
  const onCellEnter = (r, c) => { if (!isDragging || !dragStart || locked) return; setDragPath(linePath(dragStart.r, dragStart.c, r, c)); };
  const finishDrag = () => { if (!isDragging) return; if (dragPath.length > 1) tryMatch(dragPath); setIsDragging(false); setDragStart(null); setDragPath([]); };

  const gridRef = useRef(null);
  const outerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    if (!outerRef.current) return;
    const ro = new ResizeObserver(([entry]) => { setContainerW(entry.contentRect.width); });
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, []);
  const minWidth = 280;
  const availableW = Math.max(minWidth, containerW - 2);
  const winH = typeof window !== "undefined" ? window.innerHeight : 800;
  const maxGridHeight = Math.max(240, Math.floor(winH * 0.6));
  const cellSize = Math.floor(Math.min(availableW / cols, maxGridHeight / rows));

  const onTouchStart = (e) => {
    if (locked) return;
    const t = e.touches[0]; const el = gridRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const c = Math.floor((t.clientX - rect.left) / (rect.width / cols));
    const r = Math.floor((t.clientY - rect.top) / (rect.height / rows));
    if (r >= 0 && r < rows && c >= 0 && c < cols) onCellDown(r, c);
  };
  const onTouchMove = (e) => {
    if (!isDragging || locked) return;
    const t = e.touches[0]; const el = gridRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const c = Math.floor((t.clientX - rect.left) / (rect.width / cols));
    const r = Math.floor((t.clientY - rect.top) / (rect.height / rows));
    if (r >= 0 && r < rows && c >= 0 && c < cols) onCellEnter(r, c);
  };
  const onTouchEnd = () => { finishDrag(); };

  return (
    <>
      {instrucciones ? <p style={{ color: "#374151", fontSize: ".95rem", lineHeight: 1.5, marginBottom: 12 }}>{instrucciones}</p> : null}

      <div style={{ fontSize: ".9rem", lineHeight: 1.35, color: "#374151", fontWeight: 700, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 12 }}>
        <span>Resueltas: {foundFlags.filter(Boolean).length}/{rawItems.length}</span>
      </div>

      <div className="sopa-layout">
        <div className="sopa-left" ref={outerRef} style={{ width: "100%" }}>
          <div style={{ overflowX: "auto", paddingBottom: 8 }}>
            <div
              ref={gridRef}
              style={{
                width: cellSize * cols, height: cellSize * rows, display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`, userSelect: "none",
                border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden",
                touchAction: "none", boxShadow: "0 4px 16px rgba(0,0,0,.06)", background: "#fff", margin: "0 auto"
              }}
              onMouseLeave={finishDrag}
              onMouseUp={finishDrag}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchEnd}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
            >
              {Array.from({ length: rows * cols }, (_, i) => {
                const r = Math.floor(i / cols);
                const c = i % cols;
                const key = `${r},${c}`;
                const selected = dragPath.some((p) => p.r === r && p.c === c);
                const isPermanent = permanent.has(key);
                return (
                  <div
                    key={key}
                    onMouseDown={() => onCellDown(r, c)}
                    onMouseEnter={() => onCellEnter(r, c)}
                    style={{
                      width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, letterSpacing: 1, cursor: locked ? "not-allowed" : "pointer",
                      borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb",
                      background: selected ? "#bfdbfe" : isPermanent ? "#bbf7d0" : "#fff",
                      color: "#111827", fontSize: ".95rem", lineHeight: 1.2, boxSizing: "border-box",
                      opacity: locked ? .6 : 1
                    }}
                    title={`${r + 1}, ${c + 1}`}
                  >
                    {grid[r][c]}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sopa-right" style={{ width: "100%" }}>
          <h3 style={{ fontWeight: 800, marginBottom: 8, color: "#0f172a", fontSize: ".98rem", lineHeight: 1.45 }}>Preguntas</h3>
          {rawItems.map((it, i) => {
            const ok = !!foundFlags[i];
            return (
              <div key={i} className={`sopa-pregItem ${ok ? "ok" : ""}`}>
                <div className="sopa-pregItem-title">{it.pregunta || "(sin pregunta)"}</div>
                <div className={`sopa-pregItem-ans ${ok ? "ok" : ""}`}>{ok ? it.originalRespuesta : "— — —"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
        <button type="button" onClick={() => { setFinished(false); onResult(false); }} style={BTN_GHOST}>Rendirse</button>
      </div>

      <Info ok={finished} />
    </>
  );
}
